import { useState, useEffect } from 'react';

// Drive confirmation data to be stored and passed to bulk API
export interface DriveConfirmationData {
  driveStartTime: Date;
  driveEndTime: Date;
  startOdometer: number | null;
  endOdometer: number | null;
  storeCheckInTime: Date;
}

interface DriveConfirmationProps {
  // Pre-populated drive data from tracking
  estimatedDriveStart: Date | null;
  estimatedDriveEnd: Date | null;
  // Store info
  storeAddress: string;
  storeName: string;
  // Callbacks
  onConfirm: (data: DriveConfirmationData) => void;
  onCancel: () => void;
  onSkipDrive?: () => void;  // For when user didn't drive (already at store)
  isLoading?: boolean;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatDuration(startDate: Date, endDate: Date): string {
  const diffMs = endDate.getTime() - startDate.getTime();
  const diffMins = Math.round(diffMs / 60000);

  if (diffMins < 0) return '0 min';
  if (diffMins < 60) return `${diffMins} min`;

  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function parseTimeString(timeStr: string, referenceDate: Date): Date | null {
  // Parse time string like "9:30 AM" or "14:00"
  const date = new Date(referenceDate);

  // Try 12-hour format first (9:30 AM)
  const match12 = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match12) {
    let hours = parseInt(match12[1], 10);
    const mins = parseInt(match12[2], 10);
    const period = match12[3].toUpperCase();

    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    date.setHours(hours, mins, 0, 0);
    return date;
  }

  // Try 24-hour format (14:00)
  const match24 = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) {
    const hours = parseInt(match24[1], 10);
    const mins = parseInt(match24[2], 10);
    date.setHours(hours, mins, 0, 0);
    return date;
  }

  return null;
}

export default function DriveConfirmation({
  estimatedDriveStart,
  estimatedDriveEnd,
  storeAddress,
  storeName,
  onConfirm,
  onCancel,
  onSkipDrive,
  isLoading = false,
}: DriveConfirmationProps) {
  const now = new Date();

  // Initialize times from props or use defaults
  const [driveStartTime, setDriveStartTime] = useState<Date>(
    estimatedDriveStart || new Date(now.getTime() - 30 * 60000) // Default: 30 min ago
  );
  const [driveEndTime, setDriveEndTime] = useState<Date>(
    estimatedDriveEnd || now
  );
  const [checkInTime, setCheckInTime] = useState<Date>(now);

  // Odometer entries
  const [startOdometer, setStartOdometer] = useState<string>('');
  const [endOdometer, setEndOdometer] = useState<string>('');

  // Edit mode states
  const [editingStartTime, setEditingStartTime] = useState(false);
  const [editingEndTime, setEditingEndTime] = useState(false);
  const [editingCheckIn, setEditingCheckIn] = useState(false);
  const [tempTimeValue, setTempTimeValue] = useState('');

  // Validation
  const [errors, setErrors] = useState<string[]>([]);

  // Update times when props change
  useEffect(() => {
    if (estimatedDriveStart) {
      setDriveStartTime(estimatedDriveStart);
    }
    if (estimatedDriveEnd) {
      setDriveEndTime(estimatedDriveEnd);
      setCheckInTime(estimatedDriveEnd);
    }
  }, [estimatedDriveStart, estimatedDriveEnd]);

  // Calculate miles if both odometers are entered
  const calculateMiles = (): number | null => {
    const start = parseFloat(startOdometer);
    const end = parseFloat(endOdometer);
    if (!isNaN(start) && !isNaN(end) && end > start) {
      return Math.round((end - start) * 10) / 10;
    }
    return null;
  };

  const handleStartTimeEdit = () => {
    setTempTimeValue(formatTime(driveStartTime));
    setEditingStartTime(true);
  };

  const handleEndTimeEdit = () => {
    setTempTimeValue(formatTime(driveEndTime));
    setEditingEndTime(true);
  };

  const handleCheckInEdit = () => {
    setTempTimeValue(formatTime(checkInTime));
    setEditingCheckIn(true);
  };

  // #3019 — one-tap "I just arrived" that sets the arrival (drive end) to now.
  // The arrival is the value that determines drive time; defaulting it silently
  // to the check-in moment let a late check-in inflate the drive (diag #67), so
  // we make it a conscious choice: tap Now, or set the actual arrival time.
  const handleArrivedNow = () => {
    const nowDate = new Date();
    setDriveEndTime(nowDate);
    setCheckInTime(nowDate);
    setEditingEndTime(false);
  };

  const handleTimeConfirm = (type: 'start' | 'end' | 'checkin') => {
    const parsed = parseTimeString(tempTimeValue, driveStartTime);
    if (parsed) {
      if (type === 'start') {
        setDriveStartTime(parsed);
        setEditingStartTime(false);
      } else if (type === 'end') {
        setDriveEndTime(parsed);
        // Also update check-in to match drive end by default
        setCheckInTime(parsed);
        setEditingEndTime(false);
      } else {
        setCheckInTime(parsed);
        setEditingCheckIn(false);
      }
    }
    setTempTimeValue('');
  };

  const handleTimeCancel = () => {
    setEditingStartTime(false);
    setEditingEndTime(false);
    setEditingCheckIn(false);
    setTempTimeValue('');
  };

  const validate = (): boolean => {
    const newErrors: string[] = [];

    // Validate times
    if (driveEndTime <= driveStartTime) {
      newErrors.push('Drive end time must be after drive start time');
    }

    if (checkInTime < driveEndTime) {
      newErrors.push('Check-in time cannot be before arrival');
    }

    // Validate odometers if both entered
    if (startOdometer && endOdometer) {
      const start = parseFloat(startOdometer);
      const end = parseFloat(endOdometer);
      if (isNaN(start) || isNaN(end)) {
        newErrors.push('Invalid odometer reading');
      } else if (end < start) {
        newErrors.push('End odometer must be greater than start odometer');
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleConfirm = () => {
    if (!validate()) return;

    const data: DriveConfirmationData = {
      driveStartTime,
      driveEndTime,
      startOdometer: startOdometer ? parseFloat(startOdometer) : null,
      endOdometer: endOdometer ? parseFloat(endOdometer) : null,
      storeCheckInTime: checkInTime,
    };

    onConfirm(data);
  };

  const miles = calculateMiles();

  return (
    <div className="rounded-xl bg-white p-4 shadow-card">
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
          <svg className="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
        </div>
        <div>
          <h3 className="font-semibold text-slate-800">Confirm Drive Details</h3>
          <p className="text-sm text-slate-500">Arriving at {storeName}</p>
        </div>
      </div>

      {/* Drive Time Section */}
      <div className="mb-4 rounded-lg bg-slate-50 p-3">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
          Drive Time
        </div>

        {/* Start Time */}
        <div className="flex items-center justify-between py-1.5">
          <span className="text-sm text-slate-600">Started driving</span>
          {editingStartTime ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={tempTimeValue}
                onChange={(e) => setTempTimeValue(e.target.value)}
                placeholder="9:30 AM"
                className="w-24 rounded border border-slate-300 px-2 py-1 text-sm"
                autoFocus
              />
              <button onClick={() => handleTimeConfirm('start')} className="text-green-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
              <button onClick={handleTimeCancel} className="text-red-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              onClick={handleStartTimeEdit}
              className="flex items-center gap-1 text-sm font-medium text-primary-600"
            >
              {formatTime(driveStartTime)}
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}
        </div>

        {/* Arrived at store — the value that determines drive time. #3019: made
            a conscious choice (prominent + one-tap "Now") so a late check-in no
            longer silently inflates the drive (diag #67: 134 min for a 15 min
            drive). Tap Now if you just arrived, or set the actual arrival time. */}
        <div className="my-1 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-amber-900">When did you arrive?</span>
            {editingEndTime ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={tempTimeValue}
                  onChange={(e) => setTempTimeValue(e.target.value)}
                  placeholder="10:00 AM"
                  className="w-24 rounded border border-slate-300 px-2 py-1 text-sm"
                  autoFocus
                />
                <button onClick={() => handleTimeConfirm('end')} className="text-green-600">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button onClick={handleTimeCancel} className="text-red-600">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleArrivedNow}
                  className="rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                >
                  Now
                </button>
                <button
                  onClick={handleEndTimeEdit}
                  className="flex items-center gap-1 text-sm font-medium text-primary-600"
                >
                  {formatTime(driveEndTime)}
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          <p className="mt-1 text-xs text-amber-700">
            Checked in late? Tap the time to set when you <span className="font-medium">actually</span> arrived, so
            your drive time is right.
          </p>
        </div>

        {/* Duration */}
        <div className="mt-2 border-t border-slate-200 pt-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Total Drive Time</span>
            <span className="text-sm font-semibold text-slate-800">
              {formatDuration(driveStartTime, driveEndTime)}
            </span>
          </div>
        </div>
      </div>

      {/* Odometer Section */}
      <div className="mb-4 rounded-lg bg-slate-50 p-3">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
          Odometer Readings <span className="font-normal text-slate-400">(optional)</span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-slate-500">Start</label>
            <input
              type="number"
              value={startOdometer}
              onChange={(e) => setStartOdometer(e.target.value)}
              placeholder="12,345"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-500">End</label>
            <input
              type="number"
              value={endOdometer}
              onChange={(e) => setEndOdometer(e.target.value)}
              placeholder="12,367"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>

        {miles !== null && (
          <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2">
            <span className="text-sm font-medium text-slate-700">Miles Driven</span>
            <span className="text-sm font-semibold text-slate-800">{miles} mi</span>
          </div>
        )}
      </div>

      {/* Check-In Time Section */}
      <div className="mb-4 rounded-lg bg-green-50 p-3">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-green-700">
          Store Check-In
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-green-700">Check-in time</div>
            <div className="text-xs text-green-600">{storeAddress}</div>
          </div>
          {editingCheckIn ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={tempTimeValue}
                onChange={(e) => setTempTimeValue(e.target.value)}
                placeholder="10:05 AM"
                className="w-24 rounded border border-slate-300 px-2 py-1 text-sm"
                autoFocus
              />
              <button onClick={() => handleTimeConfirm('checkin')} className="text-green-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
              <button onClick={handleTimeCancel} className="text-red-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              onClick={handleCheckInEdit}
              className="flex items-center gap-1 text-sm font-semibold text-green-700"
            >
              {formatTime(checkInTime)}
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="mb-4 rounded-lg bg-red-50 p-3">
          {errors.map((error, idx) => (
            <p key={idx} className="text-sm text-red-700">{error}</p>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-2">
        <button
          onClick={handleConfirm}
          disabled={isLoading}
          className="w-full rounded-xl bg-primary-600 py-3.5 text-center font-semibold text-white shadow-lg transition-all hover:bg-primary-700 active:scale-[0.98] disabled:opacity-50"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Checking In...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Confirm & Check In
            </span>
          )}
        </button>

        {onSkipDrive && (
          <button
            onClick={onSkipDrive}
            disabled={isLoading}
            className="w-full rounded-xl border border-slate-200 bg-white py-3 text-center text-sm font-medium text-slate-600 transition-all hover:bg-slate-50 disabled:opacity-50"
          >
            I didn't drive (already at store)
          </button>
        )}

        <button
          onClick={onCancel}
          disabled={isLoading}
          className="w-full py-2 text-center text-sm font-medium text-slate-500 transition-all hover:text-slate-700 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
